"use client";

import { motion } from "framer-motion";
import Image from "next/image";
import { ShieldCheck, Sparkles, MessageSquare, Zap } from "lucide-react";
import { useTranslations } from "next-intl";

export function LoginShell({ children }: { children: React.ReactNode }) {
  const t = useTranslations("login");

  const features = [
    { icon: MessageSquare, text: t("feature1") },
    { icon: Zap, text: t("feature2") },
    { icon: Sparkles, text: t("feature3") },
  ];

  return (
    <div className="min-h-screen flex">
      {/* Left — branding */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between bg-primary px-12 py-14">
        <div className="flex items-center gap-3">
          <Image src="/header-badge.png" alt="AtendIA" width={36} height={36} className="rounded-xl" />
          <span className="text-xl font-bold text-primary-foreground tracking-tight">
            AtendIA
          </span>
        </div>

        <div className="space-y-8">
          <div>
            <h1 className="text-4xl font-bold text-primary-foreground leading-tight">
              {t("headline")}
              <br />
              <span className="text-primary-foreground/70">{t("headlineAccent")}</span>
            </h1>
            <p className="mt-4 text-primary-foreground/60 text-base leading-relaxed max-w-sm">
              {t("headlineSub")}
            </p>
          </div>

          <div className="space-y-4">
            {features.map(({ icon: Icon, text }, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 + i * 0.1, duration: 0.4 }}
                className="flex items-start gap-3"
              >
                <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-primary-foreground/15">
                  <Icon className="h-3.5 w-3.5 text-primary-foreground" />
                </div>
                <p className="text-sm text-primary-foreground/75 leading-snug pt-0.5">
                  {text}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

        <p className="text-xs text-primary-foreground/30">
          © {new Date().getFullYear()} AtendIA — {t("copyright")}
        </p>
      </div>

      {/* Right — form */}
      <div className="flex flex-1 items-center justify-center bg-background px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className="w-full max-w-sm"
        >
          {/* Mobile logo */}
          <div className="mb-8 flex items-center gap-2.5 lg:hidden">
            <Image src="/header-badge.png" alt="AtendIA" width={32} height={32} className="rounded-lg" />
            <span className="text-lg font-bold">AtendIA</span>
          </div>

          <div className="mb-7">
            <div className="mb-3 inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/5 px-2.5 py-1 text-xs font-medium text-primary">
              <ShieldCheck className="h-3.5 w-3.5" />
              {t("secureAccess")}
            </div>
            <h2 className="text-2xl font-bold text-foreground">{t("welcome")}</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              {t("welcomeSub")}
            </p>
          </div>

          {children}
        </motion.div>
      </div>
    </div>
  );
}
